import React, { useEffect, useState } from 'react';
import { Container, Card, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import authService from '../services/authService';
import {
  ArrowLeft,
  User,
  Shield,
  Lock,
  LogOut,
  ChevronRight,
} from 'lucide-react';

const UserAccountDetails = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [user, setUser] = useState(null);
  const [kycStatus, setKycStatus] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Fetch profile from API
    const fetchProfile = async () => {
      try {
        const profile = await authService.getProfile();
        if (profile) {
          setUser(profile);
          setProfilePicture(profile.profilePicture);
          setKycStatus(profile.kycStatus);
        }

        // Transaction password status check removed
      } catch (error) {
        // Don't show error toast for profile loading
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
      await logout(); // Clear context
      toast.success('Logged out successfully!', {
        position: 'bottom-left',
        autoClose: 2000,
      });
      setTimeout(() => {
        navigate('/login');
      }, 500);
    } catch (error) {
      toast.error('Error logging out. Please try again.', {
        position: 'bottom-left',
        autoClose: 3000,
      });
    }
  };

  return (
    <>
      {/* Header and Profile Section - Full Width */}
      <div
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          paddingBottom: '20px',
        }}
      >
        {/* Header */}
        <div className='position-relative pt-3 pb-3 text-white'>
          <div
            className='d-flex align-items-center'
            style={{
              maxWidth: isMobile ? '100%' : '85%',
              margin: '0 auto',
              padding: 'clamp(1rem, 3vw, 1.5rem)',
              paddingTop: 0,
              paddingBottom: 0,
            }}
          >
            <Button
              variant='link'
              onClick={() => navigate('/profile')}
              className='text-white p-0'
              aria-label='Back to Profile'
            >
              <ArrowLeft size={24} />
            </Button>
            <h5 className='mb-0 fw-bold position-absolute start-50 translate-middle-x'>
              Account Details
            </h5>
          </div>
        </div>

        {/* Profile Header Section */}
        <div className='text-center text-white px-3 pb-3'>
          <div
            className='rounded-circle border border-3 border-white d-flex align-items-center justify-content-center mx-auto mb-3'
            style={{
              width: '85px',
              height: '85px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              backgroundImage: profilePicture
                ? `url(${profilePicture})`
                : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            {!profilePicture && <User size={36} color='white' />}
          </div>

          <div>
            <h5 className='fw-bold mb-2'>{user?.email || '—'}</h5>
            <div className='d-flex justify-content-center gap-3 flex-wrap'>
              <span className='small opacity-75'>
                UID:{' '}
                {loading
                  ? '...'
                  : user?.uid || (user?.id ? user.id.split('-')[0] : '—')}
              </span>
              {user?.isEmailVerified ? (
                <span
                  className='badge px-2 py-1 fw-semibold small'
                  style={{
                    background: 'linear-gradient(45deg, #00C851, #007E33)',
                    borderRadius: '15px',
                  }}
                >
                  Verified
                </span>
              ) : (
                <span
                  className='badge px-2 py-1 fw-semibold small'
                  style={{
                    background: 'linear-gradient(45deg, #FF6B6B, #FF8A65)',
                    borderRadius: '15px',
                  }}
                >
                  Unverified
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Container - Responsive Width */}
      <Container
        fluid
        style={{
          maxWidth: isMobile ? '100%' : '85%',
          padding: 'clamp(1rem, 3vw, 1.5rem)',
          paddingTop: '24px',
          paddingBottom: '100px',
          backgroundColor: '#FFFFFF',
          minHeight: 'calc(100vh - 250px)',
        }}
      >
        {/* Verification Section */}
        <div className='mb-4'>
          <h6 className='fw-bold mb-3 text-dark'>Verification Status</h6>
          <Card
            className='border-0 shadow-sm hover-lift'
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/verification')}
          >
            <Card.Body className='d-flex align-items-center p-3'>
              <div
                className='rounded-circle me-2 me-sm-3 d-flex align-items-center justify-content-center flex-shrink-0'
                style={{
                  width: '45px',
                  height: '45px',
                  backgroundColor: '#FFA726',
                }}
              >
                <Shield size={20} color='white' />
              </div>
              <div className='flex-grow-1' style={{ minWidth: 0 }}>
                <div
                  className='fw-semibold text-dark'
                  style={{ fontSize: '0.95rem' }}
                >
                  Identity Verification
                </div>
                <small
                  className='text-muted d-block text-truncate'
                  style={{ fontSize: '0.8rem' }}
                >
                  Complete your KYC process
                </small>
              </div>
              <div
                className='d-flex align-items-center flex-shrink-0'
                style={{ gap: '0.5rem' }}
              >
                <span
                  className='badge px-2 py-1 d-none d-sm-inline'
                  style={{
                    backgroundColor:
                      kycStatus === 'APPROVED'
                        ? '#E8F5E9'
                        : kycStatus === 'PENDING'
                        ? '#FFF3E0'
                        : kycStatus === 'NOT_SUBMITTED'
                        ? '#FFEBEE'
                        : '#FFEBEE',
                    color:
                      kycStatus === 'APPROVED'
                        ? '#2E7D32'
                        : kycStatus === 'PENDING'
                        ? '#F57C00'
                        : kycStatus === 'NOT_SUBMITTED'
                        ? '#C62828'
                        : '#C62828',
                    fontSize: '0.7rem',
                  }}
                >
                  {loading
                    ? '...'
                    : kycStatus === 'APPROVED'
                    ? 'Verified'
                    : kycStatus === 'PENDING'
                    ? 'Pending'
                    : kycStatus === 'NOT_SUBMITTED'
                    ? 'Unverified'
                    : 'Unverified'}
                </span>
                <ChevronRight size={16} color='#6c757d' />
              </div>
            </Card.Body>
          </Card>
        </div>

        {/* Account Information */}
        <div className='mb-4'>
          <h6 className='fw-bold mb-3 text-dark'>Account Information</h6>
          <Card className='border-0 shadow-sm'>
            <Card.Body className='p-3 p-sm-4'>
              <div className='row g-3'>
                <div className='col-12 mb-2'>
                  <label
                    className='small text-muted fw-medium d-block mb-1'
                    style={{ fontSize: '0.75rem' }}
                  >
                    Email Address
                  </label>
                  <div
                    className='fw-semibold text-break'
                    style={{
                      fontSize: '0.9rem',
                      wordBreak: 'break-word',
                    }}
                  >
                    {loading ? '...' : user?.email || '—'}
                  </div>
                </div>
                <div className='col-12 mb-2'>
                  <label
                    className='small text-muted fw-medium d-block mb-1'
                    style={{ fontSize: '0.75rem' }}
                  >
                    User ID
                  </label>
                  <div className='fw-semibold' style={{ fontSize: '0.9rem' }}>
                    {loading
                      ? '...'
                      : user?.uid || (user?.id ? user.id.split('-')[0] : '—')}
                  </div>
                </div>
                <div className='col-6'>
                  <label
                    className='small text-muted fw-medium d-block mb-1'
                    style={{ fontSize: '0.75rem' }}
                  >
                    Account Status
                  </label>
                  <div>
                    <span
                      className={`badge ${
                        user?.status === 'ACTIVE'
                          ? 'bg-success'
                          : 'bg-secondary'
                      }`}
                      style={{ fontSize: '0.7rem' }}
                    >
                      {user?.status || '—'}
                    </span>
                  </div>
                </div>
                <div className='col-6'>
                  <label
                    className='small text-muted fw-medium d-block mb-1'
                    style={{ fontSize: '0.75rem' }}
                  >
                    Email Status
                  </label>
                  <div>
                    <span
                      className={`badge ${
                        user?.isEmailVerified
                          ? 'bg-success'
                          : 'bg-warning text-dark'
                      }`}
                      style={{ fontSize: '0.7rem' }}
                    >
                      {user?.isEmailVerified ? 'Verified' : 'Unverified'}
                    </span>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>

        {/* Security Settings */}
        <div className='mb-4'>
          <h6 className='fw-bold mb-3 text-dark'>Security Settings</h6>

          <Card
            className='mb-3 border-0 shadow-sm hover-lift'
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/login-password')}
          >
            <Card.Body
              className='d-flex align-items-center p-3'
              style={{ gap: '0.75rem' }}
            >
              <div
                className='rounded-circle d-flex align-items-center justify-content-center flex-shrink-0'
                style={{
                  width: '45px',
                  height: '45px',
                  backgroundColor: '#42A5F5',
                }}
              >
                <Lock size={20} color='white' />
              </div>
              <div className='flex-grow-1' style={{ minWidth: 0 }}>
                <div
                  className='fw-semibold text-dark'
                  style={{ fontSize: '0.95rem' }}
                >
                  Login Password
                </div>
                <small
                  className='text-muted d-block text-truncate'
                  style={{ fontSize: '0.8rem' }}
                >
                  Secure your account access
                </small>
              </div>
              <div
                className='d-flex align-items-center flex-shrink-0'
                style={{ gap: '0.5rem' }}
              >
                <ChevronRight size={16} color='#6c757d' />
              </div>
            </Card.Body>
          </Card>

          <Card
            className='mb-3 border-0 shadow-sm hover-lift'
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/transaction-password')}
          >
            <Card.Body
              className='d-flex align-items-center p-3'
              style={{ gap: '0.75rem' }}
            >
              <div
                className='rounded-circle d-flex align-items-center justify-content-center flex-shrink-0'
                style={{
                  width: '45px',
                  height: '45px',
                  backgroundColor: '#66BB6A',
                }}
              >
                <Shield size={20} color='white' />
              </div>
              <div className='flex-grow-1' style={{ minWidth: 0 }}>
                <div
                  className='fw-semibold text-dark'
                  style={{ fontSize: '0.95rem' }}
                >
                  Transaction Password
                </div>
                <small
                  className='text-muted d-block text-truncate'
                  style={{ fontSize: '0.8rem' }}
                >
                  Secure your transactions
                </small>
              </div>
              <div
                className='d-flex align-items-center flex-shrink-0'
                style={{ gap: '0.5rem' }}
              >
                <span
                  className='badge px-2 py-1 d-none d-sm-inline'
                  style={{
                    backgroundColor: '#E8F5E9',
                    color: '#2E7D32',
                    fontSize: '0.7rem',
                  }}
                >
                  Active
                </span>
                <ChevronRight size={16} color='#6c757d' />
              </div>
            </Card.Body>
          </Card>
        </div>

        {/* Logout Button */}
        <div className='pt-3 pb-4'>
          <Button
            className='w-100 py-3 fw-semibold d-flex align-items-center justify-content-center'
            style={{
              backgroundColor: '#FF6B6B',
              border: 'none',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(255, 107, 107, 0.3)',
            }}
            onClick={handleLogout}
          >
            <LogOut size={20} color='white' className='me-2' />
            Log Out
          </Button>
        </div>
      </Container>

      <style>{`
        .hover-lift {
          transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
        }
        .hover-lift:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
        }

        @media (max-width: 576px) {
          h5 {
            font-size: 1.1rem !important;
          }
          h6 {
            font-size: 1rem !important;
          }
          .card-body {
            padding: 0.75rem !important;
          }
        }

        @media (max-width: 380px) {
          h5 {
            font-size: 1rem !important;
          }
          h6 {
            font-size: 0.95rem !important;
          }
        }
      `}</style>
    </>
  );
};

export default UserAccountDetails;
